import axios from 'axios'
import { getAssetUploadCredential } from '../../../../api/upload'
import {
  ASSET_UPLOAD_BUSINESS_TYPE,
  CLOUD_MAX_RETRY,
  CLOUD_RETRY_DELAYS_MS
} from './constants'
import {
  extractErrorMessage,
  isRetryableUploadError,
  sleep
} from './uploadErrorClassify'

function finalizeCanceled(row, message = '已取消上传') {
  row.cloudStatus = 'canceled'
  row.cloudProgress = 0
  row.cloudErrorMessage = message || '已取消上传'
  row.cancelSource = null
}

/**
 * 单行上云：预签名 + PUT，含可重试错误的自动退避。
 * 取消：有 in-flight 请求时靠 row.cancelSource（axios CancelToken）；
 * 退避 sleep 期间 cancelSource 为 null，依赖弹窗把 cloudStatus 置为 canceled，
 * 本函数在循环入口与 sleep 结束后检查该状态并退出。
 */
export async function uploadRowToCloud(row) {
  if (row.cloudStatus === 'uploading') return
  if (row.cloudStatus === 'canceled') return

  row.cloudStatus = 'uploading'
  row.cloudProgress = 0
  row.cloudErrorMessage = ''
  row.cloudErrorKind = ''

  for (let attempt = 0; attempt <= CLOUD_MAX_RETRY; attempt += 1) {
    // 退避等待中用户可能已点取消：入口先认状态，避免再建 CancelToken
    if (row.cloudStatus === 'canceled') {
      finalizeCanceled(row, row.cloudErrorMessage)
      return
    }

    if (attempt > 0) {
      row.cloudRetryCount = attempt
      const delay =
        CLOUD_RETRY_DELAYS_MS[attempt - 1] ||
        CLOUD_RETRY_DELAYS_MS[CLOUD_RETRY_DELAYS_MS.length - 1]
      await sleep(delay)
      // sleep 期间 cancelSource 为空；弹窗只能改 cloudStatus，醒来必须再查一次
      if (row.cloudStatus === 'canceled') {
        finalizeCanceled(row, row.cloudErrorMessage)
        return
      }
    }

    const cancelSource = axios.CancelToken.source()
    row.cancelSource = cancelSource

    try {
      const credRes = await getAssetUploadCredential(
        {
          fileName: row.name,
          contentType: row.raw.type || 'application/octet-stream',
          businessType: ASSET_UPLOAD_BUSINESS_TYPE
        },
        { cancelToken: cancelSource.token }
      )

      if (!credRes || !credRes.success || !credRes.data) {
        throw new Error(
          (credRes && (credRes.errMessage || credRes.message)) ||
            '获取上传凭证失败'
        )
      }

      const { uploadUrl, publicUrl, headers } = credRes.data
      row.uploadUrl = uploadUrl
      row.publicUrl = publicUrl
      row.headers = headers || {}

      const headerCopy = { ...(headers || {}) }
      const ct = headerCopy['content-type'] || headerCopy['Content-Type']
      delete headerCopy['content-type']
      delete headerCopy['Content-Type']
      const contentType = ct || row.raw.type || 'application/octet-stream'

      await axios.put(uploadUrl, row.raw, {
        headers: { ...headerCopy, 'Content-Type': contentType },
        transformRequest: [(data) => data],
        cancelToken: cancelSource.token,
        onUploadProgress: (event) => {
          if (event.total) {
            row.cloudProgress = Math.round((event.loaded * 100) / event.total)
          }
        }
      })

      row.cloudProgress = 100
      row.cloudStatus = 'success'
      row.cloudRetryCount = 0
      row.uploadedAt = Date.now()
      row.cancelSource = null
      return
    } catch (error) {
      row.cancelSource = null
      if (axios.isCancel(error)) {
        finalizeCanceled(row, error.message || '已取消上传')
        return
      }

      const retryable = isRetryableUploadError(error)
      const isLastAttempt = attempt >= CLOUD_MAX_RETRY
      if (!retryable || isLastAttempt) {
        row.cloudStatus = 'failed'
        row.cloudErrorKind = retryable ? 'retryable' : 'fatal'
        row.cloudErrorMessage = extractErrorMessage(error)
        return
      }
    }
  }
}
